import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Stack, Typography, Alert, Paper, Divider, Button,
} from '@mui/material';
import {
  Warning as WarningIcon, AccountTree as ResolveIcon,
} from '@mui/icons-material';
import { AdmissionApplication, PendingGuardianCapture } from '../../../types/admissions';
import ResolveFamilyDialog from '../dialogs/ResolveFamilyDialog';
import GuardianRow from '../../families/GuardianRow';
import FamilyChildRow from '../../families/FamilyChildRow';
import StatusChip from '../../common/StatusChip';
import { familyName, matchedByPhrase, siblingDetails } from '../../families/familyFormat';
import type { ApplicationSiblings } from '../../../services/peopleService';
import { profilePath } from '../../../utils/personRef';
import { useModulePermissions } from '../../../hooks/useModulePermissions';

/**
 * Shows the applicant's identity + family linkage. Three sections:
 *   1. Applicant snapshot (name, father, phones, address, CNIC).
 *   2. Family resolution status — if pendingGuardians[] is non-empty, show
 *      a prominent "Resolve Family" callout (the actual Resolve Family
 *      dialog is PR #4 scope and lands in the next FE chunk).
 *   3. Captured guardians (either pendingGuardians raw, or the resolved
 *      Guardian records once Resolve Family has run).
 *   4. Siblings — the family's other children once it is linked; before
 *      that, the children of the family a parent's CNIC on the form belongs to.
 */

interface Props {
  app: AdmissionApplication;
  /** Loaded by the drawer, which also shows them in its header. */
  siblings: ApplicationSiblings | null;
  onChanged?: () => Promise<void> | void;
}

const CandidateFamilyTab: React.FC<Props> = ({ app, siblings, onChanged }) => {
  const { canRead, canWrite } = useModulePermissions();
  const canManage = canWrite('admissions');
  const navigate = useNavigate();
  const familyId = typeof app.familyId === 'object' ? app.familyId?._id : app.familyId;
  const resolved = !!app.familyResolvedAt;
  const pending = (app.pendingGuardians || []) as PendingGuardianCapture[];
  const [resolveOpen, setResolveOpen] = useState(false);
  const likelyFamily = siblings?.basis === 'guardian_cnic' ? siblings.family : null;

  return (
    <Stack spacing={3}>
      {/* ── Family resolution ───────────────────────────────────── */}
      <Section title="Family">
        {resolved ? (
          <Alert
            severity="success"
            variant="outlined"
            sx={{ borderRadius: 2 }}
            action={canRead('people') && familyId ? (
              <Button color="inherit" size="small" onClick={() => navigate(`/app/families?family=${familyId}`)} sx={{ textTransform: 'none' }}>
                Open family
              </Button>
            ) : undefined}
          >
            Family linked. Guardian records are in place.
          </Alert>
        ) : (
          <Alert
            severity="warning"
            variant="outlined"
            icon={<WarningIcon fontSize="small" />}
            sx={{ borderRadius: 2 }}
          >
            <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
              Family not yet resolved
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
              Guardian data was captured below but no Guardian records exist. Resolve the family
              before enrollment handover — either attach the applicant to an existing household,
              or create a new one from the captured pending guardians.
            </Typography>
            {likelyFamily && (
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                The {matchedByPhrase(siblings!.matchedGuardians)} CNIC belongs to the{' '}
                <Box component="span" fontWeight={600}>{familyName(likelyFamily)}</Box> family, already at the school.
                Resolve family will suggest linking to it.
              </Typography>
            )}
            {canManage && (
              <Button
                size="small"
                variant="contained"
                color="warning"
                disableElevation
                startIcon={<ResolveIcon fontSize="small" />}
                onClick={() => setResolveOpen(true)}
              >
                Resolve family
              </Button>
            )}
          </Alert>
        )}
      </Section>

      {/* ── Siblings ─────────────────────────────────────────────── */}
      {!!siblings?.siblings.length && (
        <Section title={likelyFamily ? 'Likely siblings' : 'Siblings'}>
          <Paper variant="outlined" sx={{ borderRadius: 2 }}>
            {siblings.siblings.map((s, i) => (
              <React.Fragment key={s._id}>
                {i > 0 && <Divider />}
                <FamilyChildRow
                  person={s}
                  details={siblingDetails(s)}
                  chip={s.application ? <StatusChip status={{ kind: 'application', value: s.application.status }} /> : undefined}
                  onOpen={canRead('students') ? () => navigate(profilePath(s)) : undefined}
                />
              </React.Fragment>
            ))}
          </Paper>
        </Section>
      )}

      {/* ── Pending guardians ───────────────────────────────────── */}
      {pending.length > 0 && (
        <Section title="Guardians (pending)">
          <Paper
            variant="outlined"
            sx={{ borderRadius: 2, p: 0 }}
          >
            {pending.map((g, i) => (
              <React.Fragment key={`${g.cnic}-${g.relation}-${i}`}>
                {i > 0 && <Divider />}
                <GuardianRow guardian={g} pending />
              </React.Fragment>
            ))}
          </Paper>
        </Section>
      )}

      {resolveOpen && (
        <ResolveFamilyDialog
          open
          application={app}
          onCancel={() => setResolveOpen(false)}
          onDone={async () => {
            setResolveOpen(false);
            if (onChanged) await onChanged();
          }}
        />
      )}
    </Stack>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Box>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, mb: 1, display: 'block' }}
    >
      {title}
    </Typography>
    <Stack spacing={0.75}>{children}</Stack>
  </Box>
);

export default CandidateFamilyTab;
